import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, Lock, User, MapPin, Landmark, Building2 } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import LegalAcceptanceStatus from "@/components/LegalAcceptanceStatus";
import PasskeySettings from "@/components/PasskeySettings";
import CalendarIcsSubscription from "@/components/CalendarIcsSubscription";

// Module-level helpers (NOT inside component) — keep stable identity so React
// doesn't unmount inputs on each keystroke.
const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3">
    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
      <Icon className="text-[#00E575]" size={18} />
      <h2 className="font-display text-lg font-semibold">{title}</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, name, type = "text", placeholder = "", full = false, autoCapitalize, inputMode }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-[12px] font-semibold text-zinc-300 mb-1.5 uppercase tracking-widest">{label}</label>
    <input
      data-testid={`profile-${name}-input`}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoCapitalize={autoCapitalize}
      inputMode={inputMode}
      className="h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-zinc-50 text-base focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
    />
  </div>
);

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      street: user.street || "",
      house_number: user.house_number || "",
      zip: user.zip || "",
      city: user.city || "",
      iban: user.iban || "",
      bic: user.bic || "",
      account_holder: user.account_holder || "",
      company_name: user.company_name || "",
      vat_id: user.vat_id || "",
      commercial_register: user.commercial_register || "",
      tax_number: user.tax_number || "",
      dob: user.dob || "",
    });
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch("/users/me", form);
      toast.success("Profil aktualisiert");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Mein Profil" back="/dashboard" />
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Read-only identifiers */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
            <Lock className="text-zinc-500" size={16} />
            <h2 className="font-display text-sm font-semibold text-zinc-400 uppercase tracking-widest">Anmelde-Daten</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Mobilnummer</div>
              <div className="text-zinc-200 font-mono" data-testid="profile-phone-readonly">{user?.phone || "—"}</div>
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">E-Mail</div>
              <div className="text-zinc-200" data-testid="profile-email-readonly">{user?.email || "—"}</div>
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Rolle</div>
              <div className="text-zinc-200">{user?.role}</div>
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Status</div>
              <div className="text-zinc-200 flex items-center gap-1.5">
                {user?.status === "active" && <CheckCircle2 size={14} className="text-[#00E575]" />}
                {user?.status}
              </div>
            </div>
          </div>
          <p className="text-[12px] text-zinc-300 mt-3">
            Änderungen an Mobilnummer oder E-Mail bitte über den Administrator anfragen.
          </p>
        </div>

        <Section icon={User} title="Persönliche Daten">
          <Field label="Vorname" name="first_name" value={form.first_name || ""} onChange={set("first_name")} />
          <Field label="Nachname" name="last_name" value={form.last_name || ""} onChange={set("last_name")} />
          <Field label="Geburtsdatum" name="dob" type="date" value={form.dob || ""} onChange={set("dob")} />
        </Section>

        <Section icon={MapPin} title="Adresse">
          {/* Iteration 87: PLZ+Ort vor Straße+Nr., konsistent mit Kunden-/
              Telesales-Erfassung (PLZ füllt Ort automatisch). */}
          <Field label="PLZ" name="zip" inputMode="numeric" value={form.zip || ""} onChange={set("zip")} />
          <Field label="Ort" name="city" full value={form.city || ""} onChange={set("city")} />
          <Field label="Straße" name="street" full value={form.street || ""} onChange={set("street")} />
          <Field label="Hausnummer" name="house_number" value={form.house_number || ""} onChange={set("house_number")} />
        </Section>

        <Section icon={Landmark} title="Bankverbindung">
          <Field label="IBAN" name="iban" full autoCapitalize="characters" value={form.iban || ""} onChange={set("iban")} />
          <Field label="BIC" name="bic" autoCapitalize="characters" value={form.bic || ""} onChange={set("bic")} />
          <Field label="Kontoinhaber" name="account_holder" value={form.account_holder || ""} onChange={set("account_holder")} />
        </Section>

        {(user?.role === "installer" || form.company_name) && (
          <Section icon={Building2} title="Geschäftlich">
            <Field label="Firmenname" name="company_name" full value={form.company_name || ""} onChange={set("company_name")} />
            <Field label="USt-ID" name="vat_id" autoCapitalize="characters" value={form.vat_id || ""} onChange={set("vat_id")} />
            <Field label="Steuernummer" name="tax_number" value={form.tax_number || ""} onChange={set("tax_number")} />
            <Field label="Handelsregister" name="commercial_register" full value={form.commercial_register || ""} onChange={set("commercial_register")} />
          </Section>
        )}

        <LegalAcceptanceStatus />

        <CalendarIcsSubscription />

        <PasskeySettings />

        <Button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={loading}
          className="h-12 w-full bg-[#00E575] text-[#09090B] font-semibold rounded-xl hover:bg-[#00C964] disabled:opacity-50 mt-2"
        >
          {loading ? <Loader2 className="animate-spin" /> : <><Save size={16} className="mr-2" /> Speichern</>}
        </Button>
      </main>
    </div>
  );
}
